import { useEffect, useState } from 'react'
import { supabase } from './supabase'

export type ProfileIdentity = { name: string; initials: string }

function initialsFor(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
}

// The signed-in account's own name and initials, what every page's
// AppHeader avatar and AgentDashboardPage's own title show. A shared hook
// rather than something threaded through each page's own session effect:
// every page already establishes its own session in its own shape (some
// gate on sessionReady, some await a reopen target first), and this needs
// nothing from any of them but the session itself. Null until the session
// and profile both resolve; a page renders its own fallback for that gap
// rather than this hook guessing one.
export function useProfileIdentity(): ProfileIdentity | null {
  const [identity, setIdentity] = useState<ProfileIdentity | null>(null)

  useEffect(() => {
    let cancelled = false
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const id = session?.user.id
      if (!id) return
      const { data: profile } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', id)
        .single()
      if (!cancelled && profile?.first_name && profile?.last_name) {
        setIdentity({
          name: `${profile.first_name} ${profile.last_name}`,
          initials: initialsFor(profile.first_name, profile.last_name),
        })
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  return identity
}
